/**
 * The catalogue API, typed at the edge of this package.
 *
 * Hand-written rather than pulled from the generated OpenAPI types, for the
 * reason the other features give: the generated schema is the authority and CI
 * fails on drift, but reaching into `components["schemas"][...]` at every call
 * site makes the code unreadable.
 *
 * This package is the **back office** half of the catalogue. The customer's
 * half lives in the portal application and talks to `/portal/catalog`, whose
 * payloads are deliberately different types — a customer's response carries no
 * completion score, no attribute source and no ERP field.
 */

import type { ApiClient } from "@hardauto/core-api";

const BASE = "/api/v1/catalog";

/**
 * The `files` module's upload endpoint.
 *
 * Named here rather than reached through `@hardauto/feature-files`, because a
 * feature may not import a sibling feature — and rather than injected as a
 * port from the composition root, because a port whose only implementation is
 * this one `POST` would put a function prop through three component layers to
 * avoid repeating a URL.
 *
 * What matters is that the *storage* is not duplicated: the bytes, the size
 * limit and the type allowlist stay in one module, and this is a call to it.
 */
const FILES = "/api/v1/storage/files";

export interface WebCategory {
  id: string;
  code: string;
  label: string;
  slug: string;
  /** The materialised ancestry, `pneumatiques/tourisme/ete`. */
  path: string;
  parent_id: string | null;
  position: number;
  description: string;
  is_active: boolean;
  /** 1 for a root category. */
  depth: number;
  /** Attribute keys offered as facets under this category, in order. */
  facet_keys: string[];
  /** The group this node gives its whole branch, when it names one itself. */
  attribute_group_id?: string | null;
}

export interface CategoryNode {
  category: WebCategory;
  /** Articles resolving to this node, excluding its descendants. */
  article_count: number;
  children: CategoryNode[];
}

/** What deleting a node took with it. */
export interface CategoryDeletion {
  /** The node and every node beneath it. */
  categories: number;
  /** Articles classed there, kept without a category of their own. */
  articles_detached: number;
  /** ERP families mapped there, kept without a mapping. */
  mappings_removed: number;
}

export interface FamilyMapping {
  family_code: string;
  family_label: string;
  article_count: number;
  required_attributes: string[];
  default_unit: string;
  category_id: string | null;
  category_path: string;
  category_label: string;
  is_mapped: boolean;
  /** The family's default brand: the last fallback for display. */
  brand?: string;
}

export interface FamilyMappingList {
  items: FamilyMapping[];
  /** How many families have no category. The number the screen leads with. */
  unmapped: number;
}

/** One completion criterion. `ratio` is what makes partial work visible. */
export interface Criterion {
  criterion: string;
  ratio: number;
  weight: number;
  /** Whether failing it prevents publication under this installation's policy. */
  blocking: boolean;
  met: boolean;
}

export interface Completion {
  score: number;
  publishable: boolean;
  criteria: Criterion[];
  /** Not yet met, in weight order — the work queue. */
  missing: string[];
  /** What actually stops publication right now. */
  blocking: string[];
}

export type MediaKind = "photo" | "document";

export interface Media {
  id: string;
  kind: MediaKind;
  file_id: string;
  label: string;
  position: number;
  is_primary: boolean;
  content_type: string;
  size_bytes: number;
}

// --- Reliability diagnostic --------------------------------------------------

export interface ReliabilityCategoryLevel {
  field_id: string;
  depth: number;
  label: string;
}

export interface ReliabilityAttributeField {
  field_id: string;
  key: string;
  label: string;
  group_label: string;
}

export interface ReliabilityFields {
  /** `core:<name>` ids — fixed, the field picker has its own labels for them. */
  core: string[];
  category_levels: ReliabilityCategoryLevel[];
  attributes: ReliabilityAttributeField[];
}

export interface ReliabilitySummary {
  total: number;
  complete: number;
  completion_pct: number;
}

export interface ReliabilityRow {
  reference: string;
  designation: string;
  /** Which of the requested fields this article does not have. */
  missing: string[];
}

export interface ReliabilityArticles {
  items: ReliabilityRow[];
  next_cursor: string | null;
}

export interface FicheRow {
  article_id: string;
  reference: string;
  designation: string;
  web_designation: string;
  brand: string;
  family_code: string;
  sales_unit: string;
  status: string;
  category_path: string;
  photo_count: number;
  document_count: number;
  completion: number;
  publishable: boolean;
  missing: string[];
  available: string;
}

export interface FicheList {
  items: FicheRow[];
  /** How many match the filters — not how many are on this page, and not how
   * many survived the completeness filter, which is applied after. */
  total: number;
  next_cursor: string | null;
}

export interface ArticleFacts {
  id: string;
  reference: string;
  designation: string;
  brand: string;
  family_code: string;
  family_label: string;
  sales_unit: string;
  base_price: string;
  status: string;
  required_attributes: string[];
  /** `key`, `value`, `source`, `is_filterable`. */
  attributes: { key: string; value: string; source: string; is_filterable: boolean }[];
  oe_references: string[];
}

export interface PublicationPolicy {
  min_photos: number;
  require_description: boolean;
  require_web_designation: boolean;
  require_category: boolean;
}

export interface Fiche {
  facts: ArticleFacts;
  web_designation: string;
  description: string;
  web_brand: string;
  /** An image given by address, `http(s)` only. Empty when there is none. */
  web_image_url: string;
  keywords: string[];
  category_id: string | null;
  category_path: string;
  category_label: string;
  /** The category came from the family mapping, not from the article. */
  category_inherited: boolean;
  photos: Media[];
  documents: Media[];
  completion: Completion;
  policy: PublicationPolicy;
  /** The group whose inputs the article screen draws — own or inherited. */
  attribute_group?: AttributeGroup | null;
  /** The article's own choice. `null` means the tree decides. */
  own_attribute_group_id?: string | null;
  /** The path of the node the group comes from; empty when it is the article's own. */
  attribute_group_inherited_from?: string;
}

export type AttributeType = "text" | "number" | "choice" | "boolean";

export interface AttributeDefinition {
  id: string;
  group_id: string;
  /** What the article's attribute carries. Never changes once values exist. */
  key: string;
  label: string;
  type: AttributeType;
  unit: string;
  options: string[];
  required: boolean;
  filterable: boolean;
  position: number;
}

export interface AttributeGroup {
  id: string;
  code: string;
  label: string;
  description: string;
  position: number;
  is_active: boolean;
  attributes: AttributeDefinition[];
}

export interface AttributeBody {
  key?: string;
  label: string;
  type: AttributeType;
  unit?: string;
  options?: string[];
  required?: boolean;
  filterable?: boolean;
}

export interface Brand {
  id: string;
  label: string;
  is_active: boolean;
  /** How many articles carry this brand. */
  article_count: number;
}

export interface CategoryLevel {
  depth: number;
  label: string;
}

export interface TreeImport {
  rows: number;
  nodes: number;
  created: number;
  already_present: number;
  brands_created: number;
}

export interface BulkPublication {
  examined: number;
  published: number;
  blocked: number;
  /** Criterion → how many articles it blocked. The actionable number. */
  blocked_by: Record<string, number>;
  examples: string[];
  next_cursor: string | null;
}

export interface FicheQuery {
  search?: string | undefined;
  categoryPath?: string | undefined;
  brand?: string | undefined;
  attributes?: readonly [string, string][] | undefined;
  publishedOnly?: boolean | undefined;
  incompleteOnly?: boolean | undefined;
  afterId?: string | undefined;
  limit?: number | undefined;
}

/**
 * One line of the back office's "Catalogue & disponibilité".
 *
 * Both halves of the same article: what a customer would be told — the price
 * their scale applies and the availability band their disclosure rule allows
 * — and what the desk sees behind it. The internal figures are `null` with no
 * depot chosen, because there is nothing to be internal about without one.
 *
 * Every quantity and price is a **string**: they are decimals, and
 * `JSON.parse` would turn 0.10 into a binary float on a screen whose whole
 * job is that two numbers agree.
 */
export interface AvailabilityRow {
  id: string;
  reference: string;
  designation: string;
  brand: string;
  sales_unit: string;
  status: string;
  /** Statut Actif/Non Actif — independent of `status`, not to be confused
   * with `sellable` below, which is a stock quantity. */
  is_active: boolean;
  unit_price: string;
  base_price: string;
  discount_pct: string;
  /** What the customer is told: `available`, `low`, `on_order`, `unknown`. */
  band: string;
  /** A number only where the article's disclosure rule allows one. */
  disclosed_quantity: string | null;
  real: string | null;
  reserved: string | null;
  forecast: string | null;
  /** On hand less what this portal's own unshipped orders have committed. */
  sellable: string | null;
}

export interface AvailabilityList {
  items: AvailabilityRow[];
  total: number;
  next_cursor: string | null;
}

export interface AvailabilityQuery extends FicheQuery {
  /** The depot the desk is asking about. Empty means "any". */
  siteCode?: string | undefined;
  /** The tariff to price under. Empty means the base price. */
  scaleCode?: string | undefined;
}

/**
 * The desk's consultation: one row per article, priced under the counter
 * tariff and stocked over the chosen scope.
 *
 * Each "no" has its own state and never a shared `null`: an article the
 * chosen company does not sell is `out_of_perimeter` on both price and
 * stock; a price that resolves to nothing is `missing`; an article no depot
 * in scope has recorded is `unknown`, which is not `zero`.
 */
export type PriceState = "priced" | "missing" | "out_of_perimeter";
export type StockState = "available" | "zero" | "unknown" | "out_of_perimeter";
export type PerimeterFilter = "in" | "out" | "all";

export interface TariffRef {
  code: string;
  label: string;
  is_default: boolean;
}

export interface ConsultationRow {
  id: string;
  reference: string;
  designation: string;
  brand: string;
  family_code: string;
  category_path: string;
  sales_unit: string;
  reference_base: string;
  code_fournisseur: string;
  ipc: string;
  status: string;
  /** Statut Actif/Non Actif, independent of `status`. */
  is_active: boolean;
  /** Every company that sells the article. */
  companies: string[];
  /** `null` in the all-companies view. */
  in_perimeter: boolean | null;
  price_state: PriceState;
  unit_price: string | null;
  stock_state: StockState;
  real: string | null;
  reserved: string | null;
  available: string | null;
  /** Stock lines in scope. */
  depots: number;
}

export interface ConsultationList {
  items: ConsultationRow[];
  total: number;
  next_cursor: string | null;
  /** The tariff the prices are under; `null` means the base price. */
  tariff: TariffRef | null;
}

export interface ConsultationQuery {
  search?: string | undefined;
  categoryPath?: string | undefined;
  /** Column filters: each matches inside that one field, and all that are given must hold. */
  reference?: string | undefined;
  designation?: string | undefined;
  referenceBase?: string | undefined;
  ipc?: string | undefined;
  /** Each of these takes several values, which are alternatives. */
  brands?: readonly string[] | undefined;
  companyCodes?: readonly string[] | undefined;
  cities?: readonly string[] | undefined;
  pointOfSaleCodes?: readonly string[] | undefined;
  siteCodes?: readonly string[] | undefined;
  perimeter?: PerimeterFilter | undefined;
  /** Only articles with stock on hand — in the chosen depots, or anywhere. */
  availableOnly?: boolean | undefined;
  /** At least this much on hand — the Quantité column's filter. */
  minAvailable?: string | undefined;
  /** Active articles, inactive ones, or all (the default). */
  active?: "all" | "active" | "inactive" | undefined;
  afterId?: string | undefined;
  limit?: number | undefined;
}

export interface DepotStock {
  site_code: string;
  site_name: string;
  company_code: string;
  company_name: string;
  point_of_sale_code: string;
  point_of_sale_name: string;
  city: string;
  real: string;
  reserved: string;
  available: string;
  forecast: string;
  /** ISO timestamp of the line's last change; null for a line never written. */
  updated_at?: string | null;
}

export interface TariffPrice {
  /** `null` for the article's own base price. */
  tariff: TariffRef | null;
  unit_price: string;
  base_price: string;
  discount_pct: string;
  state: PriceState;
}

export interface ConsultationArticle {
  id: string;
  reference: string;
  designation: string;
  brand: string;
  family_code: string;
  family_label: string;
  category_path: string;
  sales_unit: string;
  reference_base: string;
  code_fournisseur: string;
  ipc: string;
  status: string;
  /** Statut Actif/Non Actif, independent of `status`. */
  is_active: boolean;
  oe_references: string[];
  characteristics: { key: string; value: string; source: string }[];
  companies: { code: string; name: string; in_perimeter: boolean }[];
  depots: DepotStock[];
  prices: TariffPrice[];
  default_tariff: TariffRef | null;
}

export interface SaveFicheBody {
  web_designation?: string;
  description?: string;
  web_brand?: string;
  /** `http://` or `https://`, or `""` to remove it. */
  web_image_url?: string;
  category_id?: string | null;
  clear_category?: boolean;
  keywords?: string[];
  attribute_group_id?: string | null;
  /** Back to the group the category gives the article. */
  clear_attribute_group?: boolean;
}

/**
 * A query string from a flat parameter map, `attr` repeated.
 *
 * Empty strings and `false` are dropped rather than sent: the API treats an
 * absent parameter as "no filter", and sending `search=` would make an empty
 * search box a filter that matches nothing.
 */
function query(
  params: Record<string, string | number | boolean | undefined>,
  facets: readonly (readonly [string, string])[] = [],
  repeated: Record<string, readonly string[] | undefined> = {},
): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "" || value === false) continue;
    search.set(key, String(value));
  }
  // Repeated, not comma-joined: several values on the SAME key are an OR, and
  // a single parameter cannot express that.
  for (const [key, value] of facets) {
    search.append("attr", `${key}:${value}`);
  }
  // The same, for a filter that takes several values: `city=A&city=B`.
  for (const [key, values] of Object.entries(repeated)) {
    for (const value of values ?? []) if (value) search.append(key, value);
  }
  const rendered = search.toString();
  return rendered ? `?${rendered}` : "";
}

function ficheQuery(options: FicheQuery): string {
  return query(
    {
      search: options.search,
      category_path: options.categoryPath,
      brand: options.brand,
      published_only: options.publishedOnly,
      incomplete_only: options.incompleteOnly,
      after_id: options.afterId,
      limit: options.limit,
    },
    options.attributes ?? [],
  );
}

/** Every back-office catalogue call, in one place. */
export class CatalogApi {
  readonly #client: ApiClient;

  constructor(client: ApiClient) {
    this.#client = client;
  }

  // --- The tree ------------------------------------------------------------

  categoryTree(includeInactive = true): Promise<{ roots: CategoryNode[] }> {
    return this.#client.request<{ roots: CategoryNode[] }>(
      `${BASE}/categories${query({ include_inactive: includeInactive })}`,
    );
  }

  categoryList(includeInactive = true): Promise<{ items: WebCategory[] }> {
    return this.#client.request<{ items: WebCategory[] }>(
      `${BASE}/categories/flat${query({ include_inactive: includeInactive })}`,
    );
  }

  createCategory(body: {
    code: string;
    label: string;
    parent_id?: string | null;
    /** Display order among its siblings; 0 first. */
    position?: number;
    facet_keys?: string[];
    attribute_group_id?: string | null;
  }): Promise<WebCategory> {
    return this.#client.request<WebCategory>(`${BASE}/categories`, { method: "POST", body });
  }

  updateCategory(
    id: string,
    body: Partial<{
      label: string;
      slug: string;
      position: number;
      is_active: boolean;
      facet_keys: string[];
      attribute_group_id: string;
      clear_attribute_group: boolean;
    }>,
  ): Promise<WebCategory> {
    return this.#client.request<WebCategory>(`${BASE}/categories/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body,
    });
  }

  moveCategory(id: string, parentId: string | null): Promise<WebCategory> {
    return this.#client.request<WebCategory>(
      `${BASE}/categories/${encodeURIComponent(id)}/parent`,
      { method: "POST", body: { parent_id: parentId } },
    );
  }

  /** The node and its branch; articles and families classed there are kept. */
  deleteCategory(id: string): Promise<CategoryDeletion> {
    return this.#client.request<CategoryDeletion>(`${BASE}/categories/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  }

  // --- The family mapping --------------------------------------------------

  familyMappings(): Promise<FamilyMappingList> {
    return this.#client.request<FamilyMappingList>(`${BASE}/family-mappings`);
  }

  /** Returns the whole list, because mapping one family changes the count. */
  mapFamily(familyCode: string, categoryId: string, brand?: string): Promise<FamilyMappingList> {
    return this.#client.request<FamilyMappingList>(
      `${BASE}/family-mappings/${encodeURIComponent(familyCode)}`,
      {
        method: "PUT",
        body:
          brand === undefined ? { category_id: categoryId } : { category_id: categoryId, brand },
      },
    );
  }

  // --- Levels and import ---------------------------------------------------

  levels(): Promise<{ items: CategoryLevel[] }> {
    return this.#client.request<{ items: CategoryLevel[] }>(`${BASE}/category-levels`);
  }

  nameLevels(labels: string[]): Promise<{ items: CategoryLevel[] }> {
    return this.#client.request<{ items: CategoryLevel[] }>(`${BASE}/category-levels`, {
      method: "PUT",
      body: { labels },
    });
  }

  /**
   * Create the tree from a spreadsheet: one column per level, found by name.
   *
   * The level names travel as repeated `level` parameters; the brand column's
   * header is the screen's own word for it, so it is translated, not stored.
   */
  importTree(file: File, levels: string[], brandColumn: string): Promise<TreeImport> {
    const search = new URLSearchParams();
    for (const level of levels) search.append("level", level);
    if (brandColumn) search.set("brand_column", brandColumn);
    const form = new FormData();
    form.append("upload", file);
    return this.#client.upload<TreeImport>(`${BASE}/categories/import?${search.toString()}`, form);
  }

  // --- Attribute groups ----------------------------------------------------

  attributeGroups(includeInactive = true): Promise<{ items: AttributeGroup[] }> {
    return this.#client.request<{ items: AttributeGroup[] }>(
      `${BASE}/attribute-groups${query({ include_inactive: includeInactive })}`,
    );
  }

  createAttributeGroup(body: {
    code?: string;
    label: string;
    description?: string;
  }): Promise<AttributeGroup> {
    return this.#client.request<AttributeGroup>(`${BASE}/attribute-groups`, {
      method: "POST",
      body,
    });
  }

  updateAttributeGroup(
    id: string,
    body: Partial<{ label: string; description: string; is_active: boolean }>,
  ): Promise<AttributeGroup> {
    return this.#client.request<AttributeGroup>(
      `${BASE}/attribute-groups/${encodeURIComponent(id)}`,
      { method: "PATCH", body },
    );
  }

  deleteAttributeGroup(id: string): Promise<void> {
    return this.#client.request<void>(`${BASE}/attribute-groups/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  }

  addAttribute(groupId: string, body: AttributeBody): Promise<AttributeDefinition> {
    return this.#client.request<AttributeDefinition>(
      `${BASE}/attribute-groups/${encodeURIComponent(groupId)}/attributes`,
      { method: "POST", body },
    );
  }

  updateAttribute(id: string, body: Partial<AttributeBody>): Promise<AttributeDefinition> {
    return this.#client.request<AttributeDefinition>(
      `${BASE}/attributes/${encodeURIComponent(id)}`,
      { method: "PATCH", body },
    );
  }

  removeAttribute(id: string): Promise<void> {
    return this.#client.request<void>(`${BASE}/attributes/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  }

  // --- Brands --------------------------------------------------------------

  /**
   * The brand list, each with how many articles carry it, and `missing`: the
   * brands articles carry that the list has not met.
   */
  brands(includeInactive = true): Promise<{ items: Brand[]; missing?: string[] }> {
    return this.#client.request<{ items: Brand[]; missing?: string[] }>(
      `${BASE}/brands${query({ include_inactive: includeInactive })}`,
    );
  }

  /** Add to the list every brand an article carries that it does not have. */
  syncBrands(): Promise<{ created: number }> {
    return this.#client.request<{ created: number }>(`${BASE}/brands/sync`, { method: "POST" });
  }

  createBrand(label: string): Promise<Brand> {
    return this.#client.request<Brand>(`${BASE}/brands`, { method: "POST", body: { label } });
  }

  updateBrand(id: string, body: Partial<{ label: string; is_active: boolean }>): Promise<Brand> {
    return this.#client.request<Brand>(`${BASE}/brands/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body,
    });
  }

  unmapFamily(familyCode: string): Promise<void> {
    return this.#client.request<void>(`${BASE}/family-mappings/${encodeURIComponent(familyCode)}`, {
      method: "DELETE",
    });
  }

  // --- Fiches --------------------------------------------------------------

  listFiches(options: FicheQuery = {}): Promise<FicheList> {
    return this.#client.request<FicheList>(`${BASE}/fiches${ficheQuery(options)}`);
  }

  // --- Reliability diagnostic ------------------------------------------------

  reliabilityFields(): Promise<ReliabilityFields> {
    return this.#client.request<ReliabilityFields>(`${BASE}/reliability/fields`);
  }

  reliabilitySummary(fields: readonly string[]): Promise<ReliabilitySummary> {
    return this.#client.request<ReliabilitySummary>(`${BASE}/reliability/summary`, {
      method: "POST",
      body: { fields },
    });
  }

  reliabilityArticles(
    fields: readonly string[],
    options: { afterId?: string | undefined; limit?: number | undefined } = {},
  ): Promise<ReliabilityArticles> {
    const search = new URLSearchParams();
    // Repeated, like the article list's `attr`: the backend reads `field`
    // several times, once per requested field.
    for (const field of fields) search.append("field", field);
    if (options.afterId) search.set("after_id", options.afterId);
    search.set("limit", String(options.limit ?? 50));
    return this.#client.request<ReliabilityArticles>(
      `${BASE}/reliability/articles?${search.toString()}`,
    );
  }

  /**
   * What a customer would see at one depot, with the desk's own numbers.
   *
   * Deliberately the same endpoint the design's screen needs and nothing
   * more: the price and the band come from the sources the portal itself
   * uses, so the comparison on that screen is between one set of figures
   * rather than two implementations.
   */
  availability(options: AvailabilityQuery = {}): Promise<AvailabilityList> {
    const base = ficheQuery(options);
    const extra = query({ site_code: options.siteCode, scale_code: options.scaleCode }, []);
    // Two builders, one query string: `ficheQuery` owns the shared filters
    // and this adds the two this screen has of its own.
    const joined = [base.slice(1), extra.slice(1)].filter(Boolean).join("&");
    return this.#client.request<AvailabilityList>(
      `${BASE}/availability${joined ? `?${joined}` : ""}`,
    );
  }

  // --- Consultation ---------------------------------------------------------

  consultation(options: ConsultationQuery = {}): Promise<ConsultationList> {
    return this.#client.request<ConsultationList>(
      `${BASE}/consultation${query(
        {
          search: options.search,
          category_path: options.categoryPath,
          reference: options.reference,
          designation: options.designation,
          reference_base: options.referenceBase,
          ipc: options.ipc,
          perimeter: options.perimeter,
          available_only: options.availableOnly ? true : undefined,
          min_available: options.minAvailable?.trim() || undefined,
          active: options.active && options.active !== "all" ? options.active : undefined,
          after_id: options.afterId,
          limit: options.limit,
        },
        [],
        {
          brand: options.brands,
          company_code: options.companyCodes,
          city: options.cities,
          point_of_sale_code: options.pointOfSaleCodes,
          site_code: options.siteCodes,
        },
      )}`,
    );
  }

  /**
   * The brands the articles carry — what the list's brand filter offers.
   *
   * Not `brands()`: that is the managed spelling list, which starts empty and
   * stays so until somebody fills it, however many brands the articles name.
   */
  consultationBrands(): Promise<{ items: string[] }> {
    return this.#client.request<{ items: string[] }>(`${BASE}/consultation/brands`);
  }

  consultationArticle(articleId: string): Promise<ConsultationArticle> {
    return this.#client.request<ConsultationArticle>(
      `${BASE}/consultation/${encodeURIComponent(articleId)}`,
    );
  }

  /**
   * A stored file's bytes — an article's photo or technical document.
   *
   * Through the client rather than as a bare URL: the download needs the
   * session's credentials, which an `<img src>` or a plain link cannot carry.
   */
  fileBlob(fileId: string): Promise<Blob> {
    return this.#client.requestBlob(`/api/v1/files/${encodeURIComponent(fileId)}/content`);
  }

  fiche(articleId: string): Promise<Fiche> {
    return this.#client.request<Fiche>(`${BASE}/fiches/${encodeURIComponent(articleId)}`);
  }

  saveFiche(articleId: string, body: SaveFicheBody): Promise<Fiche> {
    return this.#client.request<Fiche>(`${BASE}/fiches/${encodeURIComponent(articleId)}`, {
      method: "PUT",
      body,
    });
  }

  /** Type characteristics, checked against the article's group. Empty removes. */
  setAttributes(articleId: string, values: Record<string, string>): Promise<Fiche> {
    return this.#client.request<Fiche>(
      `${BASE}/fiches/${encodeURIComponent(articleId)}/attributes`,
      { method: "PUT", body: { values } },
    );
  }

  publish(articleId: string): Promise<Fiche> {
    return this.#client.request<Fiche>(`${BASE}/fiches/${encodeURIComponent(articleId)}/publish`, {
      method: "POST",
    });
  }

  unpublish(articleId: string): Promise<Fiche> {
    return this.#client.request<Fiche>(
      `${BASE}/fiches/${encodeURIComponent(articleId)}/unpublish`,
      { method: "POST" },
    );
  }

  /** Publishes every publishable fiche matching the filter, one page at a time. */
  publishMany(options: FicheQuery = {}): Promise<BulkPublication> {
    return this.#client.request<BulkPublication>(`${BASE}/fiches/publish${ficheQuery(options)}`, {
      method: "POST",
    });
  }

  // --- Media ---------------------------------------------------------------

  /**
   * Store the bytes, then reference them.
   *
   * Two requests on purpose. `files` owns storage; this module owns what a
   * file is *for*. A single endpoint here would mean a second upload path with
   * its own limits, and one of the two would stop being maintained.
   */
  uploadFile(body: FormData): Promise<{ id: string; filename: string; content_type: string }> {
    return this.#client.request<{ id: string; filename: string; content_type: string }>(FILES, {
      method: "POST",
      formData: body,
    });
  }

  listMedia(articleId: string): Promise<Media[]> {
    return this.#client.request<Media[]>(`${BASE}/fiches/${encodeURIComponent(articleId)}/media`);
  }

  attach(
    articleId: string,
    body: { kind: MediaKind; file_id: string; label?: string },
  ): Promise<Media> {
    return this.#client.request<Media>(`${BASE}/fiches/${encodeURIComponent(articleId)}/media`, {
      method: "POST",
      body,
    });
  }

  detach(mediaId: string): Promise<void> {
    return this.#client.request<void>(`${BASE}/media/${encodeURIComponent(mediaId)}`, {
      method: "DELETE",
    });
  }

  setPrimary(articleId: string, mediaId: string): Promise<void> {
    return this.#client.request<void>(
      `${BASE}/fiches/${encodeURIComponent(articleId)}/media/${encodeURIComponent(mediaId)}/primary`,
      { method: "POST" },
    );
  }
}
